import { Alert } from '@sr/ui-library';
import { FEATURE_FLAGS } from '../constant/featureFlags';
import { useCurrentUserAuth } from '../hooks/useCurrentUserAuth';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { reduxServices } from '../reducers/reduxServices';
import { useTypedSelector } from '../stateStore';
import { readConfigString } from '../utils/helpers/configurationValue';

/**
 * Site-wide announcement banner, first used for APP-2153.
 *
 * The flag controls whether it shows; these keys hold the wording. Kept
 * separate so turning the banner off does not delete the copy.
 */
export const ANNOUNCEMENT_BANNER_CONFIG_KEYS = {
  body: 'announcement.banner.body',
  title: 'announcement.banner.title',
} as const;

export const ANNOUNCEMENT_BANNER_DEFAULT_TITLE = 'Heads up!';

export type AnnouncementBannerCopy = {
  body: string;
  title: string;
};

export const resolveAnnouncementBanner = (
  configuration?: Record<string, unknown> | null,
): AnnouncementBannerCopy | null => {
  const body = readConfigString(
    configuration,
    ANNOUNCEMENT_BANNER_CONFIG_KEYS.body,
  );

  if (!body) return null;

  const title =
    readConfigString(configuration, ANNOUNCEMENT_BANNER_CONFIG_KEYS.title) ||
    ANNOUNCEMENT_BANNER_DEFAULT_TITLE;

  return { body, title };
};

export type AnnouncementBannerProps = {
  /** Width and spacing, owned by the mount site. */
  className?: string;
};

export const AnnouncementBanner = ({ className }: AnnouncementBannerProps) => {
  const configuration = useTypedSelector(
    reduxServices.setting.selectors.configuration,
  );
  const { auth } = useCurrentUserAuth();
  const [isEnabled] = useFeatureFlag(FEATURE_FLAGS.MSR3_ANNOUNCEMENT_BANNER);

  const banner = resolveAnnouncementBanner(configuration);

  // Checking the body too, not just the flag: if the flag is turned on before
  // the copy is written, show nothing rather than an empty titled box.
  if (!isEnabled || !banner || !auth?.currentUserIsExternal) return null;

  return (
    <div className={className}>
      <Alert
        title={banner.title}
        data-testid="announcement-banner"
        // Overrides Alert's built-in role="alert". This is a standing notice,
        // not an alert — the shell remounts as you navigate, and a live region
        // would read it out again every time.
        role="note"
        variant="info"
      >
        <p className="whitespace-pre-line text-sm">{banner.body}</p>
      </Alert>
    </div>
  );
};
